import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClassContext } from '../context/ClassContext';

const AdminDashboard = () => {
    const { classes, getClasses, createClass, deleteClass, loading } = useContext(ClassContext);
    const [formData, setFormData] = useState({
        name: '',
        schedules: [{ dayOfWeek: '', startTime: '', endTime: '' }]
    });
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        getClasses();
    }, [getClasses]);

    const { name, schedules } = formData;

    const daysOfWeek = [
        { value: 'monday', label: 'Thứ Hai' },
        { value: 'tuesday', label: 'Thứ Ba' },
        { value: 'wednesday', label: 'Thứ Tư' },
        { value: 'thursday', label: 'Thứ Năm' },
        { value: 'friday', label: 'Thứ Sáu' },
        { value: 'saturday', label: 'Thứ Bảy' },
        { value: 'sunday', label: 'Chủ Nhật' }
    ];

    const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const onScheduleChange = (index, field, value) => {
        const updatedSchedules = schedules.map((schedule, i) =>
            i === index ? { ...schedule, [field]: value } : schedule
        );
        setFormData({ ...formData, schedules: updatedSchedules });
    };

    const addSchedule = () => {
        setFormData({
            ...formData,
            schedules: [...schedules, { dayOfWeek: '', startTime: '', endTime: '' }]
        });
    };

    const removeSchedule = (index) => {
        if (schedules.length > 1) {
            const updatedSchedules = schedules.filter((_, i) => i !== index);
            setFormData({ ...formData, schedules: updatedSchedules });
        }
    };

    const formatSchedules = (items) => {
        if (!items || items.length === 0) return 'Chưa có lịch học';

        const dayLabels = {
            'monday': 'Thứ Hai',
            'tuesday': 'Thứ Ba',
            'wednesday': 'Thứ Tư',
            'thursday': 'Thứ Năm',
            'friday': 'Thứ Sáu',
            'saturday': 'Thứ Bảy',
            'sunday': 'Chủ Nhật'
        };

        return items.map(schedule =>
            `${dayLabels[schedule.dayOfWeek]} ${schedule.startTime}-${schedule.endTime}`
        ).join(', ');
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const validSchedules = schedules.filter(s => s.dayOfWeek && s.startTime && s.endTime);
        if (validSchedules.length === 0) {
            alert('Vui lòng thêm ít nhất một lịch học!');
            return;
        }
        createClass({ ...formData, schedules: validSchedules });
        setFormData({ name: '', schedules: [{ dayOfWeek: '', startTime: '', endTime: '' }] });
        setShowCreateModal(false);
    };

    const onDeleteClass = (classId, className) => {
        if (window.confirm(`Bạn có chắc chắn muốn xóa lớp "${className}"? Tất cả tài liệu và dữ liệu liên quan sẽ bị xóa vĩnh viễn.`)) {
            deleteClass(classId);
        }
    };

    return (
        <div className="space-y-10">
            {/* Stats Cards */}
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {[{
                    icon: '📚',
                    title: `${Array.isArray(classes) ? classes.length : 0}`,
                    subtitle: 'Tổng số lớp học'
                }, {
                    icon: '👥',
                    title: `${Array.isArray(classes) ? classes.reduce((total, cls) => total + (Array.isArray(cls.students) ? cls.students.length : 0), 0) : 0}`,
                    subtitle: 'Tổng số học sinh'
                }, {
                    icon: '⚡',
                    title: 'Hoạt động',
                    subtitle: 'Hệ thống hoạt động tốt'
                }].map((stat) => (
                    <div
                        key={stat.subtitle}
                        className="flex items-center gap-5 rounded-2xl bg-white/80 p-6 shadow-lg shadow-indigo-100 transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl"
                    >
                        <span className="text-4xl" aria-hidden>{stat.icon}</span>
                        <div>
                            <h3 className="text-2xl font-semibold text-slate-900">{stat.title}</h3>
                            <p className="text-sm text-slate-500">{stat.subtitle}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Merged Class Management Section */}
            <div className="space-y-6 rounded-3xl bg-white/90 p-8 shadow-xl">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="flex items-center gap-3 text-2xl font-semibold text-slate-900">
                        <span aria-hidden>🚀</span>
                        Quản lý lớp học
                    </h3>
                    <button
                        type="button"
                        onClick={() => setShowCreateModal(true)}
                        className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                    >
                        <span aria-hidden>➕</span>
                        Tạo lớp mới
                    </button>
                </div>

                {loading ? (
                    <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 text-slate-500">
                        <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-500" aria-hidden />
                        <span>Đang tải danh sách lớp học...</span>
                    </div>
                ) : !Array.isArray(classes) || classes.length === 0 ? (
                    <div className="flex flex-col items-center gap-4 rounded-2xl bg-slate-50 p-10 text-center">
                        <div className="text-5xl" aria-hidden>📚</div>
                        <h4 className="text-xl font-semibold text-slate-900">Chưa có lớp học nào</h4>
                        <p className="max-w-sm text-sm text-slate-500">
                            Hãy tạo lớp học đầu tiên để bắt đầu hành trình giảng dạy cùng học viên của bạn.
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {classes.map((c) => (
                            <div
                                key={c._id}
                                className="rounded-2xl border border-slate-100 bg-white/80 p-5 shadow-lg transition hover:-translate-y-1 hover:border-indigo-200 hover:shadow-xl"
                            >
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-start justify-between gap-4">
                                        <h4 className="text-lg font-semibold text-slate-900">{c.name}</h4>
                                        <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                                            👥 {Array.isArray(c.students) ? c.students.length : 0} học sinh
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-600">
                                        📅 {formatSchedules(c.schedules || []) || c.schedule}
                                    </p>
                                    <div className="flex gap-2">
                                        <Link
                                            to={`/class/${c._id}`}
                                            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                                        >
                                            👁️ Xem chi tiết
                                        </Link>
                                        <button
                                            onClick={() => onDeleteClass(c._id, c.name)}
                                            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700"
                                        >
                                            🗑️ Xóa lớp
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Class Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
                        <div className="mb-6 flex items-center justify-between">
                            <h3 className="text-2xl font-semibold text-slate-900">
                                <span aria-hidden>➕</span> Tạo lớp học mới
                            </h3>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                            >
                                <i className="fas fa-times text-xl" aria-hidden></i>
                            </button>
                        </div>

                        <form onSubmit={onSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700">📝 Tên lớp học</label>
                                <input
                                    type="text"
                                    placeholder="Nhập tên lớp học..."
                                    name="name"
                                    value={name}
                                    onChange={onChange}
                                    required
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                />
                            </div>

                            <div className="space-y-4">
                                <div className="text-sm font-semibold text-slate-700">📅 Lịch học</div>
                                {schedules.map((schedule, index) => (
                                    <div key={index} className="space-y-4 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                        <div className="grid gap-4 md:grid-cols-3">
                                            <label className="flex flex-col text-xs font-medium text-slate-600">
                                                <span className="mb-2 text-sm">Thứ trong tuần</span>
                                                <select
                                                    value={schedule.dayOfWeek}
                                                    onChange={(e) => onScheduleChange(index, 'dayOfWeek', e.target.value)}
                                                    required
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                                >
                                                    <option value="">Chọn thứ...</option>
                                                    {daysOfWeek.map(day => (
                                                        <option key={day.value} value={day.value}>
                                                            {day.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="flex flex-col text-xs font-medium text-slate-600">
                                                <span className="mb-2 text-sm">Giờ bắt đầu</span>
                                                <input
                                                    type="time"
                                                    value={schedule.startTime}
                                                    onChange={(e) => onScheduleChange(index, 'startTime', e.target.value)}
                                                    required
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                                />
                                            </label>
                                            <label className="flex flex-col text-xs font-medium text-slate-600">
                                                <span className="mb-2 text-sm">Giờ kết thúc</span>
                                                <input
                                                    type="time"
                                                    value={schedule.endTime}
                                                    onChange={(e) => onScheduleChange(index, 'endTime', e.target.value)}
                                                    required
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                                />
                                            </label>
                                        </div>
                                        {schedules.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeSchedule(index)}
                                                className="inline-flex items-center gap-2 rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600 transition hover:bg-rose-100"
                                            >
                                                ❌ Xóa lịch
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addSchedule}
                                    className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-200"
                                >
                                    ➕ Thêm lịch học
                                </button>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-200 transition hover:bg-emerald-600"
                                >
                                    ✨ Tạo lớp học
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-medium text-slate-700 shadow transition hover:bg-slate-50"
                                >
                                    Hủy
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
